/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Popover, Button, TextStyle } from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import { SVGIcon } from '@superops/super-icons';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  EventEmitter,
  EventEmitterWithIndex,
  getCurrentUserHook,
  getTranslatedText,
  useActivityTracker,
  useCheckFeatureGating,
  useProduct
} from '@nile/components';
import { useStopwatch } from 'react-timer-hook';
import {
  CREATE_WORKLOG_FROM_TIMERS,
  GET_AI_WORKLOG_NOTE,
  GET_ALL_TIMER_ENTRIES,
  GET_AUTO_START_TIMER_INFO,
  getPersonalization,
  MLClient,
  savePersonalization,
  UPDATE_WORKLOG_TIMER_ENTRY
} from '@nile/graphql';
import TimerPopup from './TimerPopup';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { removePropertiesForItProduct } from '@nile/itsm';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

interface User {
  userId: string;
  firstName: string;
  lastName: null;
  name: string;
  email: string;
  forgotten: boolean;
}

interface WorkItem {
  id?: string;
  workId?: string;
  module?: 'TICKET' | 'PROJECT' | 'INVOICE' | 'QUOTE';
  subject?: string;
  client?: { name: string; accountId: string };
  requester?: User;
  sop?: { sopId: string; name: string };
  ticketId?: string;
}

interface TimerActionProps {
  editorRef: { current: any };
  // getSelectedText: () => string;
  workItem: WorkItem;
  froalaEditor: boolean;
  bannerKey: string;
}

interface StopwatchResult {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: (offsetTimestamp?: Date, autoStart?: boolean) => void;
}

interface TimerObject {
  timerId?: string;
  billable?: boolean;
  serviceItem?: {
    itemId: string;
    name?: string;
  };
  serviceCatalogItem?: {
    itemId: string;
    name?: string;
  };
  notes?: string;
  state?: 'RUNNING' | 'PAUSED';
  type?: 'AUTOMATIC' | 'MANUAL';
  timespent?: number;
  workItem?: WorkItem;
  running?: boolean;
  createdTime?: string;
  module?: string;
  technician?: User;
}

const timerActions = [
  {
    label: 'worklog.create.from.reply',
    value: 'createWorklog'
  },
  {
    label: 'timer.manual.create.from.reply',
    value: 'createManualTimer'
  }
  // {
  //   label: 'timer.auto.create.from.reply',
  //   value: 'createAutoTimer'
  // }
];

/**
 * @description This function captures selected text from reply editor and converts to notes in timer/worklog.
 * @param {function} - getSelectedText, returns the text selected in the editor.
 * @param {Object} - workItem, object of type WorkItem
 * @return {void}
 */
const TimerActionsFromReply = forwardRef<HTMLDivElement, TimerActionProps>(
  ({ editorRef, workItem, froalaEditor, bannerKey }, ref) => {
    const intl = useIntl();
    const timerPopupRef = useRef();
    const currentUser = getCurrentUserHook();
    const flags = useFlags();

    const [active, setActive] = useState<boolean>(false);
    const [text, setText] = useState(null);
    const [error, setError] = useState<boolean>(false);
    const [currentTimer, setCurrentTimer] = useState<TimerObject | null>(null);
    const isItProduct = useCheckIsItProduct();

    const hideTimerActionsBannerRef: { current: boolean } = useRef(false);

    const { data: autoStartConfigData } = useQuery(GET_AUTO_START_TIMER_INFO);

    const [fetchTimers, { data: timersData }] = useLazyQuery(
      GET_ALL_TIMER_ENTRIES,
      {
        fetchPolicy: 'network-only',
        variables: {
          isItProduct,
          worklogTimerInput: {
            listInfo: {
              condition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'technician.userId',
                    operator: 'is',
                    value: currentUser?.userId
                  },
                  {
                    attribute: 'workItem.workId',
                    operator: 'is',
                    value: workItem?.id
                  }
                ]
              }
            }
          }
        }
      }
    );

    let stopwatchOffset = new Date();
    stopwatchOffset.setSeconds(stopwatchOffset.getSeconds());
    const {
      seconds,
      minutes,
      hours,
      days,
      isRunning,
      reset
    }: StopwatchResult = useStopwatch({
      autoStart: false,
      offsetTimestamp: stopwatchOffset
    });

    function startTimer(timer: TimerObject): void {
      stopwatchOffset = new Date();
      stopwatchOffset.setSeconds(
        stopwatchOffset.getSeconds() + (timer?.timespent || 0)
      );
      reset(stopwatchOffset, timer?.running);
      setCurrentTimer(timer);
    }

    function stopTimer(): void {
      const stopwatchOfset = new Date();
      stopwatchOfset.setSeconds(stopwatchOfset.getSeconds());
      if (isRunning) reset(stopwatchOfset, false);
      setCurrentTimer(null);
    }

    useEffect(() => {
      if (timersData?.fetchWorklogTimerEntries?.entries?.length) {
        const runningTimer: TimerObject = timersData?.fetchWorklogTimerEntries?.entries.filter(
          (timer) => timer?.running
        )?.[0];
        if (runningTimer) startTimer(runningTimer);
        else stopTimer();
      } else stopTimer();
    }, [timersData]);

    function resetTimerActionsState() {
      setText(null);
      if (error) setError(false);
      setCurrentTimer(null);
    }

    function onCompleteHandler({
      responseTimerKey
    }: {
      responseTimerKey?: string;
    }) {
      return (res) => {
        // close the worklog action popup and reset timer state
        setActive(false);
        resetTimerActionsState();

        // Refresh global timers, ticket details page timers and extension timers.
        EventEmitter.dispatch('refetchTimerEntries');
        EventEmitter.dispatch('refetchWorklogs');

        // if response has createWorklogTimerEntry or updateWorklogTimerEntry key and the running is true start the timer
        if (res[responseTimerKey] && res[responseTimerKey]?.running) {
          startTimer(res[responseTimerKey]);
        } else {
          stopTimer();
        }
      };
    }

    // const [
    //   createWorklogTimerEntry,
    //   { loading: createWorklogTimerLoading }
    // ] = useMutation(CREATE_WORKLOG_TIMER_ENTRY, {
    //   onCompleted: onCompleteHandler({
    //     responseTimerKey: 'createWorklogTimerEntry'
    //   })
    // });

    const [
      updateWorklogTimerEntryWithInterceptor,
      { loading: updateWorklogTimerLoading }
    ] = useMutation(UPDATE_WORKLOG_TIMER_ENTRY, {
      onCompleted: onCompleteHandler({
        responseTimerKey: 'updateWorklogTimerEntry'
      })
    });

    const currentProduct = useProduct();

    const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

    async function updateWorklogTimerEntry(variables) {
      try {
        const updatedPayload = removePropertiesForItProduct(
          variables,
          'updateWorklogTimerEntry',
          currentProduct
        );
        return await updateWorklogTimerEntryWithInterceptor(updatedPayload);
      } catch (ex) {
        return ex;
      }
    }

    const [
      createWorklogEntryFromTimer,
      { loading: createWorklogEntryFromTimerLoading }
    ] = useMutation(CREATE_WORKLOG_FROM_TIMERS, {
      onCompleted: onCompleteHandler({
        responseTimerKey: 'createWorklogEntriesFromTimers'
      })
    });

    // function autoTimerHandling(text: string) {
    //   if (currentTimer) {
    //     updateWorklogTimerEntry({
    //       variables: {
    //         timerEntryInput: {
    //           timerId: currentTimer?.timerId,
    //           notes: `${currentTimer?.notes} ${text}`
    //         }
    //       }
    //     });
    //   } else {
    //     const newTimerInput: TimerObject = {
    //       billable: true,
    //       notes: text,
    //       type: 'AUTOMATIC',
    //       workItem: {
    //         workId: workItem?.workId || workItem?.id,
    //         module: workItem?.module || 'TICKET'
    //       }
    //     };
    //     if (
    //       autoStartConfigData?.getAutoStartTimerInfo.defaultServiceItem.itemId
    //     ) {
    //       newTimerInput.serviceItem = {
    //         itemId:
    //           autoStartConfigData?.getAutoStartTimerInfo.defaultServiceItem
    //             .itemId
    //       };
    //     }
    //     createWorklogTimerEntry({
    //       variables: {
    //         timerEntryInput: newTimerInput
    //       }
    //     });
    //   }
    // }

    function trackEvent(event) {
      useActivityTracker({
        event: event
      });
    }

    const aiFeatureHook = AIFeaturesHook();
    const isAINoteEnabled = aiFeatureHook?.getIsAIFeatureEnabled(
      'WORKLOG_NOTE_COMPOSE'
    ); // Check if feature is enabled in settings;
    const featureData = useCheckFeatureGating('ai_ticket_summary'); // CHECK IS AI AVAILABLE IN PLAN

    const isAiAvailable = isAINoteEnabled && featureData?.enabled;

    function manualTimerHandling(): void {
      const popoverData = {
        currentTimer: {
          notes: text,
          billable: true,
          workItem: {
            ...workItem,
            workId: workItem?.workId || workItem?.id,
            module: workItem?.module || 'TICKET'
          },
          [contractsNew
            ? 'serviceCatalogItem'
            : 'serviceItem']: autoStartConfigData?.getAutoStartTimerInfo[
            contractsNew ? 'defaultServiceCatalogItem' : 'defaultServiceItem'
          ]
        },
        onlyManual: true,
        aiWorklogNotes: isAiAvailable,
        timerNote: text || ''
      };

      // if (currentTimer) {
      //   updateWorklogTimerEntry({
      //     variables: {
      //       timerEntryInput: {
      //         timerId: currentTimer?.timerId,
      //         notes: `${currentTimer?.notes || ''} ${text}`
      //       }
      //     }
      //   }).then(({ data }) => {
      //     if (timerPopupRef?.current?.togglePopover) {
      //       timerPopupRef.current.togglePopover({
      //         ...popoverData,
      //         currentTimer: {
      //           ...data?.updateWorklogTimerEntry,
      //           type: 'MANUAL',
      //           state: 'PAUSED',
      //           running: false
      //         },
      //         onlyManual: false
      //       });
      //     }
      //   });
      // } else {
      if (timerPopupRef?.current?.togglePopover) {
        timerPopupRef.current.togglePopover(popoverData);
        trackEvent('Added manual time from reply editor');
      }
      // }
    }

    function displayError(errorMessage): void {
      if (bannerKey) {
        EventEmitter.dispatch(bannerKey, {
          message: getTranslatedText(intl, errorMessage),
          status: 'warning'
        });
      }
      setActive(false);
    }

    function createWorklogHandling(): void {
      if (isAiAvailable) {
        const popoverData = {
          currentTimer: {
            ...currentTimer,
            notes: text
          },
          onlyManual: false,
          aiWorklogNotes: isAiAvailable,
          timerNote: text || ''
        };

        if (timerPopupRef?.current?.togglePopover) {
          timerPopupRef.current.togglePopover(popoverData);
          trackEvent('Created worklog from reply editor');
        }
      } else {
        if (currentTimer?.timerId) {
          updateWorklogTimerEntry({
            variables: {
              timerEntryInput: {
                timerId: currentTimer?.timerId,
                notes: `${currentTimer?.notes || ''} ${text}`
              }
            }
          }).then((res) => {
            // manualTimerHandling();
            if (res) {
              createWorklogEntryFromTimer({
                variables: {
                  input: [
                    {
                      timerId: currentTimer?.timerId
                    }
                  ]
                }
              }).then((res) => {
                trackEvent('Created worklog from reply editor');
              });
            }
          });
        } else displayError('timer.action.from.ticket.reply.error.message');
      }
    }

    function handleActionSelect(selectedAction): void {
      if (!isEmpty(text)) {
        switch (selectedAction.value) {
          // case 'createAutoTimer':
          //   autoTimerHandling(text);
          // break;
          case 'createManualTimer':
            manualTimerHandling();
            break;
          default:
            createWorklogHandling();
            break;
        }
        setActive(false);
        if (error) setError(false);
      }
    }

    const formatTime = (time: number): string => {
      return String(time).padStart(2, '0');
    };

    function onBannerClose() {
      if (!hideTimerActionsBannerRef?.current) {
        const personlisationData = {
          name: currentUser.userId,
          category: 'TimerActionsFromReply',
          value: JSON.stringify({ hideTimerActionsFromReplyBanner: true }),
          user: {
            firstName: currentUser?.firstName,
            userId: currentUser?.userId,
            email: currentUser?.email,
            lastName: currentUser?.lastName
          }
        };
        savePersonalization({
          variables: personlisationData,
          input: personlisationData
        });
      }
    }

    useEffect(() => {
      fetchTimers();
      if (bannerKey) {
        getPersonalization({
          userId: currentUser?.userId,
          category: 'TimerActionsFromReply',
          name: currentUser?.userId
        }).then(({ data }) => {
          let value = null;
          if (data?.getPersonalizationByCategoryAndName?.value) {
            value = JSON.parse(
              data?.getPersonalizationByCategoryAndName?.value
            );
            hideTimerActionsBannerRef.current =
              value?.hideTimerActionsFromReplyBanner;
          }
          if (!value?.hideTimerActionsFromReplyBanner) {
            EventEmitter.dispatch(bannerKey);
          }
        });
      }

      const eventIndex = EventEmitterWithIndex.subscribe(
        'refetchGlobalTimers',
        fetchTimers
      );
      EventEmitter.subscribe('bannerCloseEvent', onBannerClose);
      return (): void => {
        EventEmitterWithIndex.unsubscribe('refetchGlobalTimers', eventIndex);
        EventEmitter.unsubscribe('bannerCloseEvent');
      };
    }, []);

    function adjustNewlines(inputString) {
      // When the text is from the Froala editor, the newlines are doubled. This function will remove the extra lines.
      // Sample input: "Hello\n\nWorld\n\n\nSuperops"
      // Output: "Hello\nWorld\n\nSuperops"

      // Use a regular expression to match consecutive newline characters
      return inputString.replace(/(\n+)/g, (match) => {
        const newlineCount = match.length;
        // If the count is even, divide by 2
        if (newlineCount % 2 === 0) {
          return '\n'.repeat(newlineCount / 2);
        }
        // If the count is odd, divide by 2 and add 1 more newline
        return '\n'.repeat(Math.floor(newlineCount / 2) + 1);
      });
    }

    const getSelectedText = (): string | null => {
      if (editorRef.current) {
        if (froalaEditor) {
          if (editorRef.current?.FroalaEditorRef?.current?.selection) {
            const selectedText = editorRef.current?.FroalaEditorRef?.current?.selection.text();
            const adjustedText = adjustNewlines(selectedText);
            return adjustedText;
          }
          return null;
        }

        const range = editorRef.current?.getEditorSelection();
        if (range) {
          const editor = editorRef.current?.getEditor();
          const unprivilegedEditor = editorRef.current?.makeUnprivilegedEditor(
            editor
          );
          // You may now use the unprivilegedEditor proxy methods
          return unprivilegedEditor?.getText(range.index, range.length);
        }
        return null;
      }
      return null;
    };

    return (
      <TimerPopup hideWorkitem refFromProps={timerPopupRef}>
        <div ref={ref} className="fx col g5">
          <div className="fx actr g10">
            <div
              className={`${
                isRunning ? 'timer-bg__highlight' : 'timer-bg'
              } h-38 tcenter vmiddle flex center br-5 px-10p border`}
            >
              <TextStyle weight="SemiBold">
                {formatTime(days ? days * 24 + hours : hours)}:
                {formatTime(minutes)}:{formatTime(seconds)}
              </TextStyle>
            </div>
            <Popover
              activator={
                <Button
                  onClick={() => {
                    const selectedText = getSelectedText();
                    if (selectedText) {
                      setText(selectedText);
                    }
                    setActive(!active);
                  }}
                  icon={<SVGIcon name="Worklog" size="sm" />}
                  loading={
                    updateWorklogTimerLoading ||
                    createWorklogEntryFromTimerLoading
                    // createWorklogTimerLoading ||
                  }
                />
              }
              active={active}
              onClose={() => {
                setText(null);
                if (error) setError(false);
                setActive(false);
              }}
            >
              {timerActions
                .filter((action) => {
                  if (!currentTimer) {
                    return action.value !== 'createWorklog';
                  }
                  return true;
                })
                .map((action) => {
                  return (
                    <div
                      className="custom-menu-option br-5"
                      onClick={() => {
                        if (text) {
                          handleActionSelect(action);
                        } else
                          displayError(
                            'timer.action.from.ticket.reply.error.message'
                          );
                      }}
                    >
                      <FormattedMessage id={action?.label} />
                    </div>
                  );
                })}
            </Popover>
          </div>
        </div>
      </TimerPopup>
    );
  }
);

export default TimerActionsFromReply;
